import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { SubscriptionTierCard } from './SubscriptionTierCard';
import { useEntitlements } from '@/hooks/use-entitlements';
import { useCheckout } from '@/hooks/use-checkout';
import { SUBSCRIPTION_PLANS, type SubscriptionPlanConfig } from '@/types/subscription-plans';

interface SubscriptionSelectionDialogProps {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SubscriptionSelectionDialog({
  children,
  open,
  onOpenChange,
}: SubscriptionSelectionDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const { subscription, tier } = useEntitlements();
  const { initiateSubscriptionCheckout, isLoading, error } = useCheckout();

  const isOpen = open ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;

  const isSubscriptionActive = subscription?.status === 'active' || subscription?.status === 'trialing';

  const handleSelectPlan = async (plan: SubscriptionPlanConfig) => {
    await initiateSubscriptionCheckout(plan.id);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="subscription-dialog"
      >
        <DialogHeader>
          <DialogTitle>Choose Your Plan</DialogTitle>
          <DialogDescription>
            {isSubscriptionActive
              ? `You're currently on the ${tier} plan. Upgrade or manage your subscription.`
              : 'Select a subscription plan to unlock premium features.'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" data-testid="checkout-error">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 py-4 sm:grid-cols-3">
          {SUBSCRIPTION_PLANS.map((plan) => (
            <SubscriptionTierCard
              key={plan.id}
              plan={plan}
              isCurrentPlan={isSubscriptionActive && subscription?.plan === plan.id}
              onSelect={handleSelectPlan}
              isLoading={isLoading}
              disabled={isLoading}
            />
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground">
          All plans are billed monthly. Cancel anytime.
        </p>
      </DialogContent>
    </Dialog>
  );
}
